import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Slider, message } from 'antd';
import { CheckOutlined, CloseOutlined, RedoOutlined } from '@ant-design/icons';

const ImagePositioningModal = ({
    visible,
    onCancel,
    onConfirm,
    imageFile,
    loading = false
}) => {
    const [imageUrl, setImageUrl] = useState(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [scale, setScale] = useState(1);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const canvasRef = useRef(null);
    const imageRef = useRef(null);

    // Crea URL dell'immagine quando il file cambia
    useEffect(() => {
        if (imageFile) {
            const url = URL.createObjectURL(imageFile);
            setImageUrl(url);
            return () => URL.revokeObjectURL(url);
        }
    }, [imageFile]);

    // Reset posizione quando si apre la modale
    useEffect(() => {
        if (visible) {
            setPosition({ x: 0, y: 0 });
            setScale(1);
        }
    }, [visible]);

    // Disegna l'anteprima sul canvas
    useEffect(() => {
        if (!imageUrl || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            imageRef.current = img;
            drawPreview();
        };

        img.src = imageUrl;
    }, [imageUrl, position, scale]);

    const drawPreview = () => {
        if (!canvasRef.current || !imageRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const img = imageRef.current;

        // Dimensioni del canvas (cerchio di 200px)
        const size = 200;
        canvas.width = size;
        canvas.height = size;

        // Pulisci il canvas
        ctx.clearRect(0, 0, size, size);

        // Salva il contesto per il clipping
        ctx.save();

        // Crea un cerchio per il clipping
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
        ctx.clip();

        // Calcola le dimensioni e posizione dell'immagine
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;

        // Centra l'immagine e applica la posizione
        const x = (size - scaledWidth) / 2 + position.x;
        const y = (size - scaledHeight) / 2 + position.y;

        // Disegna l'immagine
        ctx.drawImage(img, x, y, scaledWidth, scaledHeight);

        // Ripristina il contesto
        ctx.restore();

        // Disegna il bordo del cerchio
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
        ctx.strokeStyle = '#1890ff';
        ctx.lineWidth = 2;
        ctx.stroke();
    };

    // Gestione del drag
    const handleMouseDown = (e) => {
        setIsDragging(true);
        const rect = canvasRef.current.getBoundingClientRect();
        setDragStart({
            x: e.clientX - rect.left - position.x,
            y: e.clientY - rect.top - position.y
        });
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const newX = e.clientX - rect.left - dragStart.x;
        const newY = e.clientY - rect.top - dragStart.y;

        setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Aggiorna il disegno quando cambiano posizione o scala
    useEffect(() => {
        drawPreview();
    }, [position, scale]);

    // Reset posizione e scala
    const handleReset = () => {
        setPosition({ x: 0, y: 0 });
        setScale(1);
    };

    // Genera l'immagine finale ritagliata
    const generateCroppedImage = () => {
        return new Promise((resolve) => {
            if (!imageRef.current) {
                resolve(null);
                return;
            }

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = imageRef.current;
            const size = 400; // Dimensione finale più alta per qualità

            canvas.width = size;
            canvas.height = size;

            // Crea il cerchio di clipping
            ctx.beginPath();
            ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
            ctx.clip();

            // Calcola le dimensioni e posizione proporzionalmente
            const scaledWidth = img.width * scale * 2; // *2 per la dimensione maggiore
            const scaledHeight = img.height * scale * 2;
            const x = (size - scaledWidth) / 2 + position.x * 2;
            const y = (size - scaledHeight) / 2 + position.y * 2;

            // Disegna l'immagine
            ctx.drawImage(img, x, y, scaledWidth, scaledHeight);

            // Converti in blob
            canvas.toBlob(resolve, 'image/png', 0.9);
        });
    };

    // Conferma il posizionamento
    const handleConfirm = async () => {
        try {
            const croppedBlob = await generateCroppedImage();
            if (croppedBlob) {
                // Crea un file dal blob mantenendo il nome originale
                const croppedFile = new File([croppedBlob], imageFile.name, {
                    type: 'image/png'
                });
                onConfirm(croppedFile);
            } else {
                message.error("Error generating image");
            }
        } catch (error) {
            console.error("Error generating image:", error);
            message.error("Error generating image");
        }
    };

    return (
        <Modal
            title="Position Your Image"
            open={visible}
            onCancel={onCancel}
            width={600}
            footer={[
                <Button key="reset" icon={<RedoOutlined />} onClick={handleReset}>
                    Reset
                </Button>,
                <Button key="cancel" icon={<CloseOutlined />} onClick={onCancel}>
                    Cancel
                </Button>,
                <Button
                    key="confirm"
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={handleConfirm}
                    loading={loading}
                >
                    Confirm
                </Button>
            ]}
        >
            <div style={{ textAlign: 'center' }}>
                <p style={{ marginBottom: '20px', color: '#666' }}>
                    Drag the image to position it and use the slider to resize.
                    The preview shows how it will appear in the circle.
                </p>

                {/* Canvas per l'anteprima */}
                <div style={{
                    display: 'inline-block',
                    marginBottom: '20px',
                    cursor: isDragging ? 'grabbing' : 'grab'
                }}>
                    <canvas
                        ref={canvasRef}
                        width={200}
                        height={200}
                        style={{
                            border: '2px solid #e0e0e0',
                            borderRadius: '50%',
                            background: '#f5f5f5'
                        }}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                    />
                </div>

                {/* Controls */}
                <div style={{ maxWidth: '300px', margin: '0 auto' }}>
                    <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Size:
                        </label>
                        <Slider
                            min={0.5}
                            max={3}
                            step={0.1}
                            value={scale}
                            onChange={setScale}
                            marks={{
                                0.5: '50%',
                                1: '100%',
                                2: '200%',
                                3: '300%'
                            }}
                        />
                    </div>

                    <div style={{ fontSize: '14px', color: '#888', marginTop: '15px' }}>
                        <div>Position: X: {Math.round(position.x)}, Y: {Math.round(position.y)}</div>
                        <div>Scale: {Math.round(scale * 100)}%</div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ImagePositioningModal;
